/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon 
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';

const SpeciesManagement = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  const [species, setSpecies] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [newSpecies, setNewSpecies] = useState({
    name: '',
    scientific_name: '',
    facility: ''
  });

  // Pagination and search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSpecies, setFilteredSpecies] = useState([]);

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredSpecies,
    { defaultSortColumn: 'name', defaultSortOrder: 'asc' }
  );

  const fetchData = useCallback(async () => {
    try {
      const [speciesRes, facilitiesRes] = await Promise.all([
        axios.get('/api/species'),
        axios.get('/api/facilities')
      ]);
      
      let filteredSpecies = speciesRes.data;
      if (selectedFacility !== 'all') {
        filteredSpecies = filteredSpecies.filter(
          species => species.facility?._id === selectedFacility
        );
      }
      
      setSpecies(filteredSpecies);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const filtered = species.filter(sp => 
      Object.values(sp).some(value => 
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      ) ||
      sp.facility?.facility_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredSpecies(filtered);
    setPage(0);
  }, [searchQuery, species]);

  // Pagination handlers
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate pagination values
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedSpecies = sortedData.slice(startIndex, endIndex);

  // CRUD operations
  const handleCreate = async () => {
    try {
      if (!newSpecies.name || !newSpecies.scientific_name || !newSpecies.facility) {
        alert('Please fill in all required fields');
        return;
      }

      await axios.post('/api/species', newSpecies);
      setNewSpecies({ name: '', scientific_name: '', facility: '' });
      fetchData();
      toast.success('Species created successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error creating species');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/api/species/${editingSpecies._id}`, editingSpecies);
      setEditDialogOpen(false);
      setEditingSpecies(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Error updating species');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this species?')) {
      try {
        await axios.delete(`/api/species/${id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete species');
      }
    }
  };

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
      <DialogTitle>Add New Species</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={newSpecies.name}
          onChange={(e) => setNewSpecies({...newSpecies, name: e.target.value})}
        />
        <TextField
          label="Scientific Name"
          fullWidth
          margin="normal"
          value={newSpecies.scientific_name}
          onChange={(e) => setNewSpecies({...newSpecies, scientific_name: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={newSpecies.facility}
            onChange={(e) => setNewSpecies({...newSpecies, facility: e.target.value})}
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
      <DialogTitle>Edit Species</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={editingSpecies?.name || ''}
          onChange={(e) => setEditingSpecies({...editingSpecies, name: e.target.value})}
        />
        <TextField
          label="Scientific Name"
          fullWidth
          margin="normal"
          value={editingSpecies?.scientific_name || ''}
          onChange={(e) => setEditingSpecies({...editingSpecies, scientific_name: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={editingSpecies?.facility?._id || ''}
            onChange={(e) => setEditingSpecies({
              ...editingSpecies,
              facility: e.target.value
            })}
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Reference SubstrateManagement.js lines 211-258 for renderPaginationControls()
  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{ mx: 1 }}
          size="small"
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
        <span>entries</span>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page"
          onClick={() => handleChangePage(0)}
          disabled={page === 0}
        >
          <FirstPage />
        </IconButton>
        <IconButton aria-label="Previous page"
          onClick={() => handleChangePage(page - 1)}
          disabled={page === 0}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Box sx={{ mx: 2 }}>
          Page {page + 1} of {Math.ceil(filteredSpecies.length / rowsPerPage)}
        </Box>
        <IconButton aria-label="Next page"
          onClick={() => handleChangePage(page + 1)}
          disabled={page >= Math.ceil(filteredSpecies.length / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton aria-label="Last page"
          onClick={() => handleChangePage(Math.ceil(filteredSpecies.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(filteredSpecies.length / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="species-management">
      <h2>Species Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search species..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={() => setCreateDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Add New Species
      </Button>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader column="name" label="Name" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="scientific_name" label="Scientific Name" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="facility.facility_name" label="Facility" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedSpecies.map(species => (
            <tr key={species._id}>
              <td>{species.name}</td>
              <td>{species.scientific_name}</td>
              <td>{species.facility?.facility_name}</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => {
                    setEditingSpecies(species);
                    setEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(species._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}

      <Box sx={{ mt: 2, color: 'text.secondary' }}>
        Showing {startIndex + 1} to {endIndex} of {sortedData.length} entries
        {searchQuery && ` (filtered from ${species.length} total entries)`}
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
    </div>
  );
};

export default SpeciesManagement;